import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import numeral from "numeral";
import Dropdown from "./navigation/Dropdown";
import USAMap from "./map";
import CapexByCategory from "./stackedBar";
import SpendingOverTime from "./lineChart";
import mapData from "./data/mapData";
import { states } from "./data";

const Highcharts = window.Highcharts;

/*

helper function to colour states for map
  [
    ["state_id", bool => false], => use other states forecast - light blue
    ["state_id", bool => true] => has own forecast - dark blue
  ]

*/
const shadeStatesFromForecast = () => (states.map(state => [state.state_id, state.has_forecast]));

const CURRENT_YEAR = "2026";

class App extends React.Component {
  constructor(props) {
    super(props);

    const allData = [];
    props.data.Forecast.forEach(line => {
      const getStateId = (name) => {
        if(name === "Rest of USA") {
          return "other";
        } else if (name === "United States") {
          return "all";
        } else {
          const state = states.find(state => state.state_name === name);
          return state && state.state_id;
        }
      };

      const getStateName = (name) => {
        if(name === "Rest of USA") {
          return "Other states";
        } else if (name === "United States") {
          return "Whole of the US";
        } else {
          return name;
        }
      };
      if (line[1] !== "State") {
        const obj = {
          state_name: getStateName(line[1]),
          state_id: getStateId(line[1]),
          has_forecast: true,
          expenditure_type: line[2].toLowerCase(),
          service: line[3].toLowerCase(),
          sector: line[4].toLowerCase(),
          forecast: line.slice(5)
        };
        allData.push(obj);
      }
    });

    this.state = {
      allData,
      selectedStateId: "",
      selectedStateName: "",
      isOtherState: false,
      currentForecast: {},
      spendingOverTime: {},
      sumForTitle: "",
      mapOptions: {
        chart: {
          height: 300,
          width: 400,
        },
        title: {
          text: undefined
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false
        },
        tooltip: {
          animation: true,
          backgroundColor: "transparent",
          borderWidth: 0,
          formatter: function() {
            const calculateSumForTooltip = val => {
              let stateData, currForecast;
              stateData = props.data.Forecast.filter(line => line[1] === val);

              const thisYearsValues = (path) => {
                const found = stateData.find(x => x[2].toLowerCase() === path[0] && x[3].toLowerCase() === path[1] && x[4].toLowerCase() === path[2]);
                return Number((found && found[20])) || 0;
              };

              if (stateData.length > 0) {
                currForecast = {
                  wastewater_capex: {
                    sludge_management: thisYearsValues(["capex", "wastewater", "sludge management"]),
                    wastewater_networks: thisYearsValues(["capex", "wastewater", "wastewater networks"]),
                    wastewater_treatment_plants: thisYearsValues(["capex", "wastewater", "wastewater treatment plants"])
                  },
                  water_capex: {
                    seawater: thisYearsValues(["capex", "water", "seawater and brackish water desalination"]),
                    water_networks: thisYearsValues(["capex", "water", "water networks"]),
                    water_resources: thisYearsValues(["capex", "water", "water resources"]),
                    water_treatment_plants: thisYearsValues(["capex", "water", "water treatment plants"])
                  },
                  wastewater_opex: {
                    customer_services: thisYearsValues(["opex", "wastewater", "customer services"]),
                    wastewater_networks: thisYearsValues(["opex", "wastewater", "wastewater networks"]),
                    water_treatment_plants: thisYearsValues(["opex", "wastewater", "water treatment plants"])
                  },
                  water_opex: {
                    customer_services: thisYearsValues(["opex", "water", "customer services"]),
                    seawater: thisYearsValues(["opex", "water", "seawater and brackish water desalination"]),
                    water_networks: thisYearsValues(["opex", "water", "water networks"]),
                    water_resources: thisYearsValues(["opex", "water", "water resources"]),
                    water_treatment_plants: thisYearsValues(["opex", "water", "water treatment plants"]),
                  }
                };
                const totalForTip = _.sumBy(_.values(currForecast.water_capex)) + _.sumBy(_.values(currForecast.wastewater_capex));
                return `$${numeral(totalForTip).format("0,0")}m`;
              }
              stateData = props.data.Forecast.filter(line => line[1].toLowerCase() === "rest of usa");

              currForecast = {
                wastewater_capex: {
                  sludge_management: thisYearsValues(["capex", "wastewater", "sludge management"]),
                  wastewater_networks: thisYearsValues(["capex", "wastewater", "wastewater networks"]),
                  wastewater_treatment_plants: thisYearsValues(["capex", "wastewater", "wastewater treatment plants"])
                },
                water_capex: {
                  seawater: thisYearsValues(["capex", "water", "seawater and brackish water desalination"]),
                  water_networks: thisYearsValues(["capex", "water", "water networks"]),
                  water_resources: thisYearsValues(["capex", "water", "water resources"]),
                  water_treatment_plants: thisYearsValues(["capex", "water", "water treatment plants"])
                },
                wastewater_opex: {
                  customer_services: thisYearsValues(["opex", "wastewater", "customer services"]),
                  wastewater_networks: thisYearsValues(["opex", "wastewater", "wastewater networks"]),
                  water_treatment_plants: thisYearsValues(["opex", "wastewater", "water treatment plants"])
                },
                water_opex: {
                  customer_services: thisYearsValues(["opex", "water", "customer services"]),
                  seawater: thisYearsValues(["opex", "water", "seawater and brackish water desalination"]),
                  water_networks: thisYearsValues(["opex", "water", "water networks"]),
                  water_resources: thisYearsValues(["opex", "water", "water resources"]),
                  water_treatment_plants: thisYearsValues(["opex", "water", "water treatment plants"]),
                }
              };
              const totalForTip = _.sumBy(_.values(currForecast.water_capex)) + _.sumBy(_.values(currForecast.wastewater_capex));
              return `Other states $${numeral(totalForTip).format("0,0")}m`;
            };
            return `
              <div style="box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.3);">
                <div style="background-color: white; padding: 10px; border-top: 8px solid ${this.color}">
                <b>${this.key}</b></br>
                <div>${calculateSumForTooltip(this.point.properties.name)}</div>
                </div>
              </div>
            `;
          },
          hideDelay: 100,
          shadow: false,
          shape: "square",
          style: { color: "black", padding: 0 },
          useHTML: true,
        },
        legend: {
          enabled: false,
        },
        colorAxis: {
          dataClasses: [{
            from: -1,
            to: 0.01,
            color: "#b9e1f8",
          }, {
            from: 0.1,
            to: 1,
            color: "#3585b9",
          }]
        },
        plotOptions: {
          series: {
            events: {
              click: event => this.selectStateFromMap(event.point["hc-key"])
            }
          }
        },
        series: [{
          cursor: "pointer",
          mapData: mapData,
          data: shadeStatesFromForecast(),
          allowPointSelect: true,
          states: {
            select: {
              color: "#c84646",
              borderColor: "#cccccc"
            }
          },
        }]
      },
    };
    this.onSelectChange = this.onSelectChange.bind(this);
    this.selectStateFromMap = this.selectStateFromMap.bind(this);
  }

  onSelectChange(e) {
    this.selectStateFromMap(e);
  }

  selectStateFromMap(e) {
    let stateData, currentForecast, sumForTitle, selectedStateName, allYearsForecast, isOtherState;
    let spendingOverTime = {
      wastewater_capex: [],
      wastewater_opex: [],
      water_capex: [],
      water_opex: [],
    };

    stateData = this.state.allData.filter(({state_id}) => state_id === e);

    // get the state's current year forecast
    const thisYearsValues = (path) => {
      const found = stateData.find(x => x.expenditure_type === path[0] && x.service === path[1] && x.sector === path[2]);
      return Number((found && found.forecast && found.forecast[15])) || 0;
    };

    // get the state's full forecast
    const allYearsValues = (path) => {
      const found = stateData.find(x => x.expenditure_type === path[0] && x.service === path[1] && x.sector === path[2]);
      return (found && found.forecast.map(x => Number(x))) || [];
    };

    const sumArrays = (...arrays) => {
      const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
      const result = Array.from({ length: n });
      return result.map((_, i) => arrays.map(xs => xs[i] || 0).reduce((sum, x) => sum + x, 0));
    };

    if (stateData.length > 0) {
      selectedStateName = stateData[0].state_name;
      isOtherState = false;

      currentForecast = {
        wastewater_capex: {
          sludge_management: thisYearsValues(["capex", "wastewater", "sludge management"]),
          wastewater_networks: thisYearsValues(["capex", "wastewater", "wastewater networks"]),
          wastewater_treatment_plants: thisYearsValues(["capex", "wastewater", "wastewater treatment plants"])
        },
        water_capex: {
          seawater: thisYearsValues(["capex", "water", "seawater and brackish water desalination"]),
          water_networks: thisYearsValues(["capex", "water", "water networks"]),
          water_resources: thisYearsValues(["capex", "water", "water resources"]),
          water_treatment_plants: thisYearsValues(["capex", "water", "water treatment plants"])
        },
        wastewater_opex: {
          customer_services: thisYearsValues(["opex", "wastewater", "customer services"]),
          wastewater_networks: thisYearsValues(["opex", "wastewater", "wastewater networks"]),
          water_treatment_plants: thisYearsValues(["opex", "wastewater", "water treatment plants"])
        },
        water_opex: {
          customer_services: thisYearsValues(["opex", "water", "customer services"]),
          seawater: thisYearsValues(["opex", "water", "seawater and brackish water desalination"]),
          water_networks: thisYearsValues(["opex", "water", "water networks"]),
          water_resources: thisYearsValues(["opex", "water", "water resources"]),
          water_treatment_plants: thisYearsValues(["opex", "water", "water treatment plants"]),
        }
      };

      allYearsForecast = {
        wastewater_capex: {
          sludge_management: allYearsValues(["capex", "wastewater", "sludge management"]),
          wastewater_networks: allYearsValues(["capex", "wastewater", "wastewater networks"]),
          wastewater_treatment_plants: allYearsValues(["capex", "wastewater", "wastewater treatment plants"])
        },
        water_capex: {
          seawater: allYearsValues(["capex", "water", "seawater and brackish water desalination"]),
          water_networks: allYearsValues(["capex", "water", "water networks"]),
          water_resources: allYearsValues(["capex", "water", "water resources"]),
          water_treatment_plants: allYearsValues(["capex", "water", "water treatment plants"])
        },
        wastewater_opex: {
          customer_services: allYearsValues(["opex", "wastewater", "customer services"]),
          wastewater_networks: allYearsValues(["opex", "wastewater", "wastewater networks"]),
          water_treatment_plants: allYearsValues(["opex", "wastewater", "wastewater treatment plants"])
        },
        water_opex: {
          customer_services: allYearsValues(["opex", "water", "customer services"]),
          seawater: allYearsValues(["opex", "water", "seawater and brackish water desalination"]),
          water_networks: allYearsValues(["opex", "water", "water networks"]),
          water_resources: allYearsValues(["opex", "water", "water resources"]),
          water_treatment_plants: allYearsValues(["opex", "water", "water treatment plants"]),
        }
      };

      spendingOverTime.water_capex = sumArrays(...Object.values(allYearsForecast.water_capex));
      spendingOverTime.water_opex = sumArrays(...Object.values(allYearsForecast.water_opex));
      spendingOverTime.wastewater_opex = sumArrays(...Object.values(allYearsForecast.wastewater_opex));
      spendingOverTime.wastewater_capex = sumArrays(...Object.values(allYearsForecast.wastewater_capex));
    } else {
      stateData = this.state.allData.filter(({state_id}) => state_id === "other");
      selectedStateName = states.find(({state_id}) => state_id === e).state_name;
      isOtherState = true;

      currentForecast = {
        wastewater_capex: {
          sludge_management: thisYearsValues(["capex", "wastewater", "sludge management"]),
          wastewater_networks: thisYearsValues(["capex", "wastewater", "wastewater networks"]),
          wastewater_treatment_plants: thisYearsValues(["capex", "wastewater", "wastewater treatment plants"])
        },
        water_capex: {
          seawater: thisYearsValues(["capex", "water", "seawater and brackish water desalination"]),
          water_networks: thisYearsValues(["capex", "water", "water networks"]),
          water_resources: thisYearsValues(["capex", "water", "water resources"]),
          water_treatment_plants: thisYearsValues(["capex", "water", "water treatment plants"])
        },
        wastewater_opex: {
          customer_services: thisYearsValues(["opex", "wastewater", "customer services"]),
          wastewater_networks: thisYearsValues(["opex", "wastewater", "wastewater networks"]),
          water_treatment_plants: thisYearsValues(["opex", "wastewater", "water treatment plants"])
        },
        water_opex: {
          customer_services: thisYearsValues(["opex", "water", "customer services"]),
          seawater: thisYearsValues(["opex", "water", "seawater and brackish water desalination"]),
          water_networks: thisYearsValues(["opex", "water", "water networks"]),
          water_resources: thisYearsValues(["opex", "water", "water resources"]),
          water_treatment_plants: thisYearsValues(["opex", "water", "water treatment plants"]),
        }
      };

      allYearsForecast = {
        wastewater_capex: {
          sludge_management: allYearsValues(["capex", "wastewater", "sludge management"]),
          wastewater_networks: allYearsValues(["capex", "wastewater", "wastewater networks"]),
          wastewater_treatment_plants: allYearsValues(["capex", "wastewater", "wastewater treatment plants"])
        },
        water_capex: {
          seawater: allYearsValues(["capex", "water", "seawater and brackish water desalination"]),
          water_networks: allYearsValues(["capex", "water", "water networks"]),
          water_resources: allYearsValues(["capex", "water", "water resources"]),
          water_treatment_plants: allYearsValues(["capex", "water", "water treatment plants"])
        },
        wastewater_opex: {
          customer_services: allYearsValues(["opex", "wastewater", "customer services"]),
          wastewater_networks: allYearsValues(["opex", "wastewater", "wastewater networks"]),
          water_treatment_plants: allYearsValues(["opex", "wastewater", "wastewater treatment plants"])
        },
        water_opex: {
          customer_services: allYearsValues(["opex", "water", "customer services"]),
          seawater: allYearsValues(["opex", "water", "seawater and brackish water desalination"]),
          water_networks: allYearsValues(["opex", "water", "water networks"]),
          water_resources: allYearsValues(["opex", "water", "water resources"]),
          water_treatment_plants: allYearsValues(["opex", "water", "water treatment plants"]),
        }
      };

      spendingOverTime.water_capex = sumArrays(...Object.values(allYearsForecast.water_capex));
      spendingOverTime.water_opex = sumArrays(...Object.values(allYearsForecast.water_opex));
      spendingOverTime.wastewater_opex = sumArrays(...Object.values(allYearsForecast.wastewater_opex));
      spendingOverTime.wastewater_capex = sumArrays(...Object.values(allYearsForecast.wastewater_capex));

    }

    sumForTitle = numeral(_.sumBy(_.values(currentForecast.water_capex)) + _.sumBy(_.values(currentForecast.wastewater_capex))).format("0,0");

    this.setState({
      selectedStateId: e,
      selectedStateName,
      currentForecast,
      sumForTitle,
      spendingOverTime,
      isOtherState,
    });
  }

  render() {

    const {
      selectedStateId,
      selectedStateName,
      sumForTitle,
      mapOptions,
      currentForecast,
      spendingOverTime,
      isOtherState,
    } = this.state;

    return (
      <div id="usa-state-forecast">
        <div id="navigation">
          <Dropdown
           options={states}
           onChange={this.onSelectChange}
           selectedStateId={selectedStateId}
           selectedStateName={selectedStateName}
          />
        </div>
        <div id="visualisation-container">
          <div id="left-side">
            <div className="map-title">
              {selectedStateName && !isOtherState && `${selectedStateName} $${sumForTitle}m`}
              {selectedStateName && isOtherState && `${selectedStateName}, Other states $${sumForTitle}m`}
            </div>
            <div className="us-map">
              <USAMap options={mapOptions} highcharts={Highcharts} selectedState={selectedStateId} />
            </div>
            <div className="stacked-bar-container">
              {selectedStateName &&
                <CapexByCategory
                  data={currentForecast}
                  selectedStateName={selectedStateName}
                  currentYear={CURRENT_YEAR}
                  isOtherState={isOtherState}
                />
              }
            </div>
          </div>
          <div id="right-side">
            <div className="line-chart">
              {selectedStateName ?
                <SpendingOverTime
                  data={spendingOverTime}
                  selectedStateName={selectedStateName}
                  isOtherState={isOtherState}
                />
                :
                <div>
                  <div className="intro-text">Click on a state to see the forecast</div>
                  <div className="fake-border" />
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }
}

App.propTypes = {
  data: PropTypes.shape({
    Forecast: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)).isRequired
  }).isRequired,
};

export default App;
